import React, { useState, useRef, useEffect } from 'react';
import { Edit2, ChevronDown } from 'lucide-react';

const PrimarySelectField = ({ 
  label, 
  value = '', 
  onChange,
  placeholder = 'Select an option',
  options = [],
  valueKey = 'id',
  labelKey = 'name'
}:any) => {
  // const [value, setValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef:any = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event:any) => {
      if (containerRef.current && !containerRef.current?.contains(event.target)) {
        setIsOpen(false);
        setIsEditing(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleEditClick = () => {
    setIsEditing(true);
    setIsOpen(true);
  };

  const handleOptionClick = (optionValue:any) => {
    // setValue(optionValue);
    setIsOpen(false);
    setIsEditing(false);
    if (onChange) {
      onChange(optionValue);
    }
  };

  const getDisplayValue = () => {
    if (!value) return '';
    
    const isObjectArray = options.length > 0 && typeof options[0] === 'object';
    
    if (isObjectArray) {
      const selectedOption = options.find((opt:any) => opt[valueKey] === value);
      return selectedOption ? selectedOption[labelKey] : '';
    }
    
    return value;
  };

  const getOptionValue = (option:any) => {
    return typeof option === 'object' ? option[valueKey] : option;
  };

  const getOptionLabel = (option:any) => {
    return typeof option === 'object' ? option[labelKey] : option;
  };

  const containerStyle:any = {
    width: '100%',
    marginBottom: '16px',
    position: 'relative'
  };

  const labelStyle: any = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#666',
    textAlign: 'left'
  };

  const fieldContainerStyle:any = {
    position: 'relative',
    borderRadius: '8px',
    padding: '12px 48px 12px 12px',
    backgroundColor: isHovered ? '#e8e8e8' : '#f5f5f5',
    transition: 'background-color 0.2s ease',
    cursor: 'pointer'
  };

  const displayTextStyle:any = {
    color: value ? '#333' : '#999',
    fontSize: '1rem',
    userSelect: 'none'
  };

  const iconContainerStyle:any = {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
    transition: 'transform 0.2s ease'
  };

  const chevronStyle = {
    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.2s ease'
  };

  const buttonStyle:any = {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    padding: '8px',
    border: 'none',
    background: 'transparent',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s ease'
  };

  const dropdownStyle:any = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    maxHeight: '240px',
    overflowY: 'auto',
    zIndex: 1000,
    padding: '4px'
  };

  const optionStyle = (isSelected:any, isHovering:any) => ({
    padding: '10px 12px',
    cursor: 'pointer',
    borderRadius: '6px',
    fontSize: '1rem',
    color: '#333',
    backgroundColor: isHovering ? '#e8e8e8' : 'transparent',
    transition: 'background-color 0.15s ease',
    fontWeight: isSelected ? 500 : 400
  });

  return (
    <div style={containerStyle} ref={containerRef}>
      {label && (
        <label style={labelStyle}>
          {label}
        </label>
      )}
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => {
          if (!isEditing) {
            handleEditClick();
          } else {
            setIsOpen(!isOpen);
          }
        }}
        style={fieldContainerStyle}
      >
        <div className='textAlignLeft' style={displayTextStyle}>
          {getDisplayValue() || placeholder}
        </div>
        
        {isEditing ? (
          <div style={iconContainerStyle}>
            <div style={chevronStyle}>
              <ChevronDown size={18} color="#666" />
            </div>
          </div>
        ) : (
          isHovered && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick();
              }}
              style={buttonStyle}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Edit2 size={18} color="#666" />
            </button>
          )
        )}
      </div>

      {isOpen && (
        <div style={dropdownStyle}>
          {options.map((option:any, index:any) => {
            const optionValue = getOptionValue(option);
            const isSelected = optionValue === value;
            
            return (
              <OptionItem
                key={index}
                option={option}
                isSelected={isSelected}
                optionStyle={optionStyle}
                onClick={() => handleOptionClick(optionValue)}
                label={getOptionLabel(option)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// Separate component to handle individual option hover state
const OptionItem = ({ option, isSelected, optionStyle, onClick, label }:any) => {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <div
      style={optionStyle(isSelected, isHovering)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={onClick}
      className='textAlignLeft'
    >
      {label}
    </div>
  );
};

export default PrimarySelectField;